import { StreamEvent } from '@/lib/types';
import { FlagCountryCode } from '@opaz/ui-components';
import { clsx, type ClassValue } from 'clsx';
import { ar, enUS, Locale } from 'date-fns/locale';
import dayjs from 'dayjs';
import parsePhoneNumber from 'libphonenumber-js';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getLocalizedValue(map: Record<string, string>, locale: string) {
  const keys = Object.keys(map);
  const localizedValue = map[locale] || map[keys[0]];

  if (!localizedValue) {
    console.warn(`No localized value found for locale: ${locale}`);
    return '';
  }

  return localizedValue;
}

export function getLocalizedName(nameEn: string | undefined, nameAr: string | undefined, language: string) {
  if (language === 'en' && nameEn) {
    return nameEn;
  }

  return nameAr;
}

export function formatDateTime(isoDate: string, language: string) {
  if (!isoDate) {
    return '-';
  }

  return dayjs(isoDate).format(language === 'ar' ? 'YYYY/MM/DD HH:mm' : 'DD/MM/YYYY HH:mm');
}

export function formatIsoDate(isoDate: string, language: string) {
  if (!isoDate) {
    return '-';
  }

  return dayjs(isoDate).format(language === 'ar' ? 'YYYY/MM/DD' : 'DD/MM/YYYY');
}

export function getLocalNumberFromFullNumber(fullNumber: string) {
  const phoneNumberData = parsePhoneNumber(fullNumber);

  if (phoneNumberData?.isValid()) {
    return phoneNumberData.nationalNumber;
  }

  return null;
}

export function getCountryCodeFromFullNumber(fullNumber: string): FlagCountryCode | null {
  const phoneNumberData = parsePhoneNumber(fullNumber);

  if (phoneNumberData?.isValid()) {
    return phoneNumberData.country as FlagCountryCode;
  }

  return null;
}

export function formatMoney(
  value: number | string | null | undefined,
  options: {
    currency?: string | null;
    decimals?: number;
    showCurrency?: boolean;
    currencyPosition?: 'prefix' | 'suffix';
  } = {},
): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const { currency = 'OMR', decimals = 3, showCurrency = true, currencyPosition = 'suffix' } = options;

  // Parse the input value
  let numValue: number;
  if (typeof value === 'string') {
    // Remove spaces and commas for parsing
    const cleanedValue = value.replace(/[ ,]/g, '');
    numValue = parseFloat(cleanedValue);
  } else {
    numValue = value;
  }

  // Handle NaN
  if (isNaN(numValue)) {
    return '';
  }

  // Check if the number has any decimal part
  const hasDecimalPart = numValue % 1 !== 0;

  // Format the number with thousand separators and appropriate decimal places
  const formattedNumber = hasDecimalPart
    ? numValue.toLocaleString('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: decimals,
      })
    : numValue.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

  // Return the formatted string with or without currency code
  if (showCurrency && currency) {
    return currencyPosition === 'prefix' ? `${currency} ${formattedNumber}` : `${formattedNumber} ${currency}`;
  }

  return formattedNumber;
}

export function formatPercentage(value: number, language: string) {
  if (value === null || value === undefined) {
    return '-';
  }

  const locale = language === 'ar' ? 'ar-OM' : 'en-US';

  // Calculate the percentage value
  const percentValue = value * 100;

  // Determine appropriate decimal places
  const decimalPlaces = Number.isInteger(percentValue) ? 0 : percentValue.toString().split('.')[1]?.length || 0;

  const formattedValue = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.min(decimalPlaces, 3),
  }).format(percentValue);

  return `${formattedValue}%`;
}

export function getLocaleObject(language: string): Locale {
  switch (language) {
    case 'en':
      return enUS;
    case 'ar':
      return ar;
    default:
      return enUS; // Default to English if the language is not recognized
  }
}

export function downloadFile(blob: Blob, filename: string) {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(link.href);
}

export function getAvatarInitials(name: string | undefined) {
  if (!name) {
    return '';
  }

  const [firstName, lastName] = name.split(' ');

  let initials = '';

  if (firstName && lastName) {
    initials = `${firstName.charAt(0)}${lastName.charAt(0)}`;
  } else {
    initials = firstName?.charAt(0) ?? '';
  }

  return initials;
}

/**
 * Flattens nested translation objects for dot notation access
 * Converts { "attachment": { "fileActions": "value" } } to { "attachment.fileActions": "value" }
 */
export function flattenTranslations(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
  const flattened: Record<string, string> = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        // Recursively flatten nested objects
        Object.assign(flattened, flattenTranslations(obj[key] as Record<string, unknown>, newKey));
      } else {
        // Add the final value
        flattened[newKey] = String(obj[key]);
      }
    }
  }

  return flattened;
}

export function formatRent(value: number | string): string {
  const number = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(number)) return 'Invalid';

  return Math.round(number).toLocaleString('en-US');
}

export function formatDecimal(value: number | string): string {
  const number = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(number)) return 'Invalid';

  return number.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function parseLine(line: string): StreamEvent | null {
  if (!line.startsWith('data:')) return null;
  const payload = line.slice(5).trim();
  if (!payload) return null;
  try {
    return JSON.parse(payload) as StreamEvent;
  } catch {
    console.log('[parse error] invalid JSON line', { line });
    return null;
  }
}
